<template>
  <section class="financial__group-section financial__billing" aria-labelledby="financial-billing-title">
    <h2 id="financial-billing-title" tabindex="-1">Bill {{ name }}</h2>
    <div class="financial__filters">
      <div>
        <label for="financial-billing-from">Imported from</label>
        <input
          id="financial-billing-from"
          v-model="from"
          class="form-control"
          type="date"
          :max="to || undefined"
          :disabled="busy"
          aria-describedby="financial-billing-period-hint"
        />
      </div>
      <div>
        <label for="financial-billing-to">Imported through · cut-off</label>
        <input
          id="financial-billing-to"
          v-model="to"
          class="form-control"
          type="date"
          :min="from || undefined"
          :disabled="busy"
          aria-describedby="financial-billing-period-hint"
        />
      </div>
      <label class="financial__check financial__billing-new">
        <input v-model="onlyNew" type="checkbox" :disabled="busy" /> Only new amounts and corrections
      </label>
    </div>
    <p id="financial-billing-period-hint" class="financial__field-hint">
      Import dates and booking dates use Swedish calendar days. Include older unbilled rows and corrections through the cut-off,
      even when the carrier charge date is earlier. Already billed rows are left out by default.
    </p>
    <p class="financial__field-hint">Each new charge belongs to its own billing period. Billing a shipment does not close it to later fees or credits.</p>
    <div class="financial__price-grid financial__price-grid--two" role="group" aria-label="Separate price sources">
      <div><span>Quoted customer prices (SEK)</span><strong>{{ quotedAmount }}</strong><small>Original booking prices, once per shipment. Kept independently of carrier invoices.</small></div>
      <div><span>Actual carrier cost</span><strong>{{ lines.length ? amount(totals.cost, lines) : '—' }}</strong><small>Imported cost rows in this selection · internal only. Later costs can still arrive.</small></div>
    </div>
    <dl class="financial__metrics financial__metrics--compact">
      <div><dt>Cost rows to reconcile</dt><dd><strong>{{ lines.length }}</strong><small>{{ invoiceCount }} {{ invoiceCount === 1 ? 'invoice' : 'invoices' }}</small></dd></div>
      <div><dt>Quoted bookings to bill</dt><dd><strong>{{ quotedShipments.length }}</strong><small>Carrier delivery invoice pending</small></dd></div>
      <div><dt>Amount in this export</dt><dd><strong>{{ billingAmount }}</strong><small>New amounts plus approved debits or credits after previous billing</small></dd></div>
    </dl>
    <p v-if="quotedShipments.length" class="financial__message">Exporting approves the {{ quotedShipments.length }} quoted booking amounts. They are recorded so the quote is deducted when the delivery's customer amount is approved later.</p>
    <details v-if="quotedShipments.length" class="financial__billing-quotes" @toggle="toggleQuotes">
      <summary>Review {{ quotedShipments.length }} quoted bookings</summary>
      <template v-if="quotesExpanded">
        <div class="financial__table-wrap">
          <table>
            <thead><tr><th scope="col">Shipment</th><th scope="col">Your reference</th><th scope="col">Booked</th><th scope="col">Quoted amount (SEK)</th></tr></thead>
            <tbody>
              <tr v-for="shipment in visibleQuotes" :key="shipment.itemId">
                <td>{{ shipment.trackingNumbers.join(', ') }}</td><td>{{ shipment.itemReference }}</td>
                <td>{{ localDate(shipment.bookedAt) }}</td><td>{{ amountLabel(shipment.bookedPrice, 'SEK') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="financial__actions">
          <button class="financial__button" type="button" :disabled="quotePage === 1" @click="previousQuotes">Previous</button>
          <span>Page {{ quotePage }} of {{ quotePages }}</span>
          <button class="financial__button" type="button" :disabled="quotePage >= quotePages" @click="nextQuotes">Next</button>
        </div>
      </template>
    </details>
    <p v-if="billing.error" class="financial__alert financial__alert--error" role="alert">{{ billing.error }}</p>
    <!-- What the export leaves out. The rows are approved in the grid above, on this page. -->
    <p v-if="groups.open.length" id="financial-billing-open-rows" class="financial__alert" role="status">
      <mdicon name="alert-outline" :size="18" aria-hidden="true" /> {{ openText }}
    </p>
    <p v-if="groups.credits.length" class="financial__alert" role="status">
      <mdicon name="cash-refund" :size="18" aria-hidden="true" />
      {{ groups.credits.length }} previously billed {{ groups.credits.length === 1 ? 'row is' : 'rows are' }} excluded or reopened ({{ amount(creditTotal, groups.credits) }} previously billed). Reapprove the corrected customer amount to export its difference, or reconcile it manually in Jeeves; do not do both.
    </p>
    <p v-if="groups.changed.length" class="financial__alert" role="status">
      <mdicon name="swap-horizontal" :size="18" aria-hidden="true" />
      {{ groups.changed.length }} previously billed {{ groups.changed.length === 1 ? 'row has' : 'rows have' }} changed. This export contains only the approved difference; a negative amount is a credit.
    </p>
    <p v-if="onlyNew && groups.billed.length" class="financial__field-hint">
      {{ groups.billed.length }} already billed {{ groups.billed.length === 1 ? 'row is' : 'rows are' }} left out.
    </p>
    <p v-if="currencies.length > 1" class="financial__alert financial__alert--error" role="alert">
      The selected rows use several currencies ({{ currencies.map((currency) => currency || 'unconfirmed').join(', ') }}). Narrow the period or bill each currency separately.
    </p>
    <div class="financial__actions financial__billing-actions">
      <button
        ref="billingButton"
        class="financial__button financial__button--primary"
        type="button"
        :disabled="!exportable"
        @click="exportBilling"
      >
        <mdicon name="download" aria-hidden="true" /> Export billing basis (internal)
      </button>
      <button
        ref="specificationButton"
        class="financial__button"
        type="button"
        :disabled="!exportable"
        aria-describedby="financial-billing-specification-hint"
        @click="exportSpecification"
      >
        <mdicon name="download" aria-hidden="true" /> Export customer specification
      </button>
      <small id="financial-billing-specification-hint" class="financial__field-hint">Tracking numbers, quoted prices and customer amounts; no carrier costs</small>
    </div>
    <p class="financial__field-hint">Downloads are recorded here; Jeeves is not updated.</p>
    <div
      v-if="openPrompt"
      class="financial__confirm"
      role="alertdialog"
      aria-labelledby="financial-billing-open-title"
      aria-describedby="financial-billing-open-text"
    >
      <div>
        <strong id="financial-billing-open-title">{{ groups.open.length }} {{ groups.open.length === 1 ? 'row is' : 'rows are' }} not approved</strong>
        <p id="financial-billing-open-text">
          The export would contain {{ lines.length }} approved cost rows and {{ quotedShipments.length }} quoted bookings for this customer and period.
          Approve the open rows above first, or export only the approved rows.
        </p>
      </div>
      <div class="financial__actions">
        <button id="financial-billing-open-export" class="financial__button financial__button--primary" type="button" :disabled="busy" @click="confirmOpen">
          Export approved rows and quoted bookings
        </button>
        <button class="financial__button" type="button" :disabled="busy" @click="cancel">Cancel</button>
      </div>
    </div>
    <div
      v-if="prompt"
      class="financial__confirm"
      role="alertdialog"
      aria-labelledby="financial-billing-again-title"
      aria-describedby="financial-billing-again-text"
    >
      <div>
        <strong id="financial-billing-again-title">{{ billedCount }} of these rows or quoted bookings were already billed</strong>
        <p id="financial-billing-again-text">Exporting them again can bill the customer twice.</p>
      </div>
      <div class="financial__actions">
        <button
          v-if="fresh.length || freshQuotes"
          id="financial-billing-again-new"
          class="financial__button financial__button--primary"
          type="button"
          :disabled="busy"
          @click="confirm(false)"
        >
          Export new amounts only
        </button>
        <button id="financial-billing-again-include" class="financial__button" type="button" :disabled="busy" @click="confirm(true)">
          Include billed rows again
        </button>
        <button class="financial__button" type="button" :disabled="busy" @click="cancel">Cancel</button>
      </div>
    </div>
    <!-- The result of an export, next to the buttons that started it. -->
    <p v-if="failure" class="financial__alert financial__alert--error" role="alert">{{ failure }}</p>
    <p v-else-if="notice" class="financial__message" role="status">{{ notice }}</p>
  </section>
</template>
<script lang="ts">
import type { FinancialBillingSelection, FinancialImport, FinancialLedgerEntry, FinancialQuoteBilling, FinancialLine, FinancialLineState } from '@/types/Models/Financial'
import { computed, nextTick, watch, defineComponent, ref, type ComputedRef, type PropType, type Ref } from 'vue'
import { financialCommonCurrency, financialAmountLabel, financialPercent, sumFinancialAmounts } from '@/services/FinancialAmounts'
import { financialBillingPlan, financialQuoteCandidates } from '@/services/FinancialBilling'
import { financialChargeAmount, financialLocalDate } from '@/services/FinancialImport'
import { financialCustomerLines, financialLinesTotals } from '@/services/FinancialSummary'

export default defineComponent({
  name: 'FinancialCustomerBilling',
  props: {
    imports: { type: Array as PropType<FinancialImport[]>, required: true },
    entries: { type: Array as PropType<FinancialLedgerEntry[]>, default: () => [] },
    quotes: { type: Array as PropType<FinancialQuoteBilling[]>, default: () => [] },
    busy: { type: Boolean, default: false },
    // The billing customer of the page this section is on, and its name.
    customer: { type: String, required: true },
    name: { type: String, required: true },
    // The period carries over from one customer to the next.
    period: {
      type: Object as PropType<Omit<FinancialBillingSelection, 'customerId'>>,
      default: () => ({ from: '', to: '', onlyNew: true }),
    },
    // Each open row's approval state as the customer's review shows it.
    state: {
      type: Function as PropType<(line: FinancialLine) => FinancialLineState>,
      default: () => ({ open: true, edited: false, exclude: false, eligible: false, reason: null, price: null }),
    },
    // What the last export did, or why it failed.
    notice: { type: String, default: '' },
    failure: { type: String, default: '' },
  },
  emits: ['export', 'select'],
  setup(props, { emit }) {
    const from: Ref<string> = ref(props.period.from)
    const to: Ref<string> = ref(props.period.to)
    const onlyNew: Ref<boolean> = ref(props.period.onlyNew)
    const selection: ComputedRef<FinancialBillingSelection> = computed(() => ({
      customerId: props.customer,
      from: from.value,
      to: to.value,
      onlyNew: onlyNew.value,
    }))
    const prompt: Ref<'billing' | 'specification' | null> = ref(null)
    const openPrompt: Ref<'billing' | 'specification' | null> = ref(null)
    const billingButton: Ref<HTMLButtonElement | null> = ref(null)
    const specificationButton: Ref<HTMLButtonElement | null> = ref(null)
    const quotesExpanded: Ref<boolean> = ref(false)
    const quotePage: Ref<number> = ref(1)
    const quotePages: ComputedRef<number> = computed(() => Math.max(1, Math.ceil(quotedShipments.value.length / 25)))
    const visibleQuotes: ComputedRef<ReturnType<typeof financialQuoteCandidates>> = computed(() => quotedShipments.value.slice((quotePage.value - 1) * 25, quotePage.value * 25))
    const toggleQuotes = (event: Event): void => { quotesExpanded.value = (event.target as HTMLDetailsElement).open }
    const previousQuotes = (): void => { quotePage.value = Math.max(1, quotePage.value - 1) }
    const nextQuotes = (): void => { quotePage.value = Math.min(quotePages.value, quotePage.value + 1) }
    const groups: ComputedRef<ReturnType<typeof financialCustomerLines>> = computed(() => financialCustomerLines(props.imports, selection.value))
    // Changed rows contribute only the difference from prior billing. Explicit re-exports repeat the saved amount.
    const fresh: ComputedRef<FinancialLine[]> = computed(() => [...groups.value.changed, ...groups.value.ready])
    const lines: ComputedRef<FinancialLine[]> = computed(() =>
      onlyNew.value ? fresh.value : [...groups.value.billed, ...fresh.value],
    )
    const freshQuotes: ComputedRef<number> = computed(() => financialQuoteCandidates(props.entries, props.quotes, { ...selection.value, onlyNew: true }).length)
    const billedCount: ComputedRef<number> = computed(() => groups.value.billed.length + quotedShipments.value.filter((shipment) => props.quotes.some((quote) => quote.itemId === shipment.itemId)).length)
    const totals: ComputedRef<ReturnType<typeof financialLinesTotals>> = computed(() => financialLinesTotals(lines.value))
    const quotedShipments: ComputedRef<ReturnType<typeof financialQuoteCandidates>> = computed(() => financialQuoteCandidates(props.entries, props.quotes, selection.value))
    const quotedAmount: ComputedRef<string> = computed(() => {
      const prices = new Map(lines.value.filter(({ row }) => row.match).map(({ row }) => [row.match!.itemId, row.match!.bookedPrice]))
      quotedShipments.value.forEach((shipment) => prices.set(shipment.itemId, shipment.bookedPrice))
      return financialAmountLabel(prices.size && [...prices.values()].every((price) => price !== null) ? sumFinancialAmounts([...prices.values()] as string[]) : null, 'SEK')
    })
    const billing: ComputedRef<{ amount: string | null; error: string }> = computed(() => {
      try {
        const plan = financialBillingPlan(lines.value, quotedShipments.value, props.quotes, props.imports, { createdAt: '', userName: '', fileName: '' })
        return { amount: sumFinancialAmounts([...Object.values(plan.amounts).map((item) => item.amount), ...quotedShipments.value.map((shipment) => shipment.bookedPrice!)]), error: '' }
      } catch (error) {
        return { amount: null, error: error instanceof Error ? error.message : 'Billing history needs a check.' }
      }
    })
    const billingAmount: ComputedRef<string> = computed(() => currencies.value.length === 1 ? financialAmountLabel(billing.value.amount, currencies.value[0]) : '—')
    const marginPercent: ComputedRef<string | null> = computed(() => (totals.value.cost === null || totals.value.margin === null ? null : financialPercent(totals.value.margin, totals.value.cost)))
    const currencies: ComputedRef<string[]> = computed(() => [...new Set([...lines.value.map((line) => line.batch.currency), ...quotedShipments.value.map(() => 'SEK')])])
    const invoiceCount: ComputedRef<number> = computed(() => new Set(lines.value.map((line) => line.batch.id)).size)
    const openCost: ComputedRef<string> = computed(() =>
      sumFinancialAmounts(
        groups.value.open
          .map((line) => financialChargeAmount(line.batch, line.row))
          .filter((value): value is string => value !== null),
      ),
    )
    const readyToApprove: ComputedRef<FinancialLine[]> = computed(() =>
      groups.value.open.filter((line) => props.state(line).eligible && !props.state(line).exclude),
    )
    const toExclude: ComputedRef<FinancialLine[]> = computed(() => groups.value.open.filter((line) => props.state(line).eligible && props.state(line).exclude))
    const needsCheck: ComputedRef<FinancialLine[]> = computed(() => groups.value.open.filter((line) => !props.state(line).eligible))
    const readyToApproveTotal: ComputedRef<string> = computed(() => sumFinancialAmounts(readyToApprove.value.map((line) => props.state(line).price!.amount)))
    const currencyText: ComputedRef<string> = computed(() => currencies.value.map((currency) => currency || 'unconfirmed').join(', ') || '—')
    // What the credited rows were billed at, from the export that billed them.
    const creditTotal: ComputedRef<string> = computed(() =>
      sumFinancialAmounts(
        groups.value.credits.map(({ batch, row }) => {
          const record = [...batch.exports].reverse().find((item) => item.kind !== 'review' && item.rowIds?.includes(row.id))
          return record?.snapshot?.[row.id]?.customerPrice ?? '0'
        }),
      ),
    )
    const exportable: ComputedRef<boolean> = computed(() => !props.busy && !billing.value.error && (lines.value.length + quotedShipments.value.length) > 0 && currencies.value.length === 1)
    const amount = (value: string | null, rows: FinancialLine[]): string => {
      const currency = financialCommonCurrency(rows.map((line) => line.batch.currency))
      return currency === null ? 'Mixed or unconfirmed currencies' : financialAmountLabel(value, currency)
    }
    // One line: how many rows the export leaves out, and why.
    const openText: ComputedRef<string> = computed(() => {
      const open = groups.value.open.length
      const check = needsCheck.value.length
      const parts = [
        readyToApprove.value.length ? `${readyToApprove.value.length} ready (${amount(readyToApproveTotal.value, readyToApprove.value)})` : '',
        toExclude.value.length ? `${toExclude.value.length} marked not to bill` : '',
        check ? `${check} ${check === 1 ? 'needs' : 'need'} a check` : '',
        `open carrier cost ${amount(openCost.value, groups.value.open)}`,
      ].filter(Boolean)
      return `${open} ${open === 1 ? 'row' : 'rows'} not approved, so not in this export: ${parts.join(' · ')}`
    })
    watch(selection, (value) => {
      quotePage.value = 1
      emit('select', value)
      prompt.value = null
      openPrompt.value = null
    })
    // A billing basis with open rows is incomplete: ask before exporting only the approved part.
    const start = async (kind: 'billing' | 'specification', openChecked = false): Promise<void> => {
      if (!openChecked && groups.value.open.length) {
        openPrompt.value = kind
        await nextTick()
        document.getElementById('financial-billing-open-export')?.focus()
        return
      }
      if (!onlyNew.value && billedCount.value) {
        prompt.value = kind
        await nextTick()
        const target =
          document.getElementById('financial-billing-again-new') ?? document.getElementById('financial-billing-again-include')
        target?.focus()
        return
      }
      emit('export', kind, selection.value, lines.value)
    }
    // A closed prompt hands focus back to the export button that opened it. Once an export has started, directly or from
    // a prompt, focus goes to the heading instead: the buttons are disabled while it runs, and stay so when nothing is
    // left to bill.
    const focusAfterPrompt = async (kind: 'billing' | 'specification' | null): Promise<void> => {
      await nextTick()
      const button = kind === 'billing' ? billingButton.value : kind === 'specification' ? specificationButton.value : null
      const target = button ?? document.getElementById('financial-billing-title')
      target?.focus()
    }
    // A prompt takes the focus itself; an export that starts right away moves it to the heading.
    const exportKind = async (kind: 'billing' | 'specification'): Promise<void> => {
      await start(kind)
      if (!prompt.value && !openPrompt.value) await focusAfterPrompt(null)
    }
    const exportBilling = (): Promise<void> => exportKind('billing')
    const exportSpecification = (): Promise<void> => exportKind('specification')
    const confirm = (includeBilled: boolean): void => {
      const kind = prompt.value
      prompt.value = null
      if (!kind) return
      emit('export', kind, { ...selection.value, onlyNew: !includeBilled }, includeBilled ? lines.value : fresh.value)
      void focusAfterPrompt(null)
    }
    const cancel = (): void => {
      const kind = prompt.value ?? openPrompt.value
      prompt.value = null
      openPrompt.value = null
      void focusAfterPrompt(kind)
    }
    const confirmOpen = async (): Promise<void> => {
      const kind = openPrompt.value
      openPrompt.value = null
      if (!kind) return
      await start(kind, true)
      // Billed rows in the export open the next prompt, which takes the focus itself.
      if (!prompt.value) await focusAfterPrompt(null)
    }
    return {
      quotesExpanded,
      quotePage,
      quotePages,
      visibleQuotes,
      toggleQuotes,
      previousQuotes,
      nextQuotes,
      localDate: financialLocalDate,
      amountLabel: financialAmountLabel,
      quotedAmount,
      quotedShipments,
      freshQuotes,
      billedCount,
      billing,
      billingAmount,
      from,
      to,
      onlyNew,
      groups,
      creditTotal,
      fresh,
      prompt,
      openPrompt,
      billingButton,
      specificationButton,
      confirm,
      confirmOpen,
      cancel,
      openText,
      lines,
      totals,
      marginPercent,
      currencies,
      invoiceCount,
      currencyText,
      exportable,
      amount,
      exportBilling,
      exportSpecification,
    }
  },
})
</script>
